<script setup>
// Modal de hora de inicio de jornada · monitoreo (Victor 2026-06-09, P3).
// Se muestra a TODOS los agentes (no super_admin) la 1ra vez que entran cada día.
// El backend (#status) registra la hora de entrada y decide si sale. Bloquea el
// sistema: la única acción es "Entendido · iniciar labor". Si dan clic en otro
// lugar, el botón vibra y aparece una flecha (mismo tema que el 911 de atrasos).
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import WorkStartAPI from 'dashboard/api/workStart';

const store = useStore();
const visible = ref(false);
const saving = ref(false);
const startedAtLabel = ref('');
const dateLabel = ref('');
let ran = false;

const nagging = ref(false);
const shaking = ref(false);
let nagTimer = null;
function enforce() {
  if (saving.value) return;
  nagging.value = true;
  shaking.value = false;
  requestAnimationFrame(() => {
    shaking.value = true;
  });
  clearTimeout(nagTimer);
  nagTimer = setTimeout(() => {
    nagging.value = false;
    shaking.value = false;
  }, 2600);
}

const currentAccount = computed(() => store.getters.getCurrentAccount || null);
const isSuperAdmin = computed(() => currentAccount.value?.super_admin === true);

async function maybeShow() {
  if (isSuperAdmin.value) return;
  try {
    const { data } = await WorkStartAPI.status();
    if (data?.pending) {
      startedAtLabel.value = data.started_at_label || '';
      dateLabel.value = data.date_label || '';
      visible.value = true;
    }
  } catch (e) {
    /* silencioso */
  }
}

async function confirmar() {
  if (saving.value) return;
  saving.value = true;
  try {
    await WorkStartAPI.confirm();
    visible.value = false;
  } catch (e) {
    saving.value = false;
  }
}

watch(
  currentAccount,
  acc => {
    if (acc && acc.id && !ran) {
      ran = true;
      maybeShow();
    }
  },
  { immediate: true }
);
</script>

<template>
  <div v-if="visible" class="ws-overlay" @click="enforce">
    <div class="ws-card">
      <div class="ws-pad">
        <div class="ws-lbl"><span class="ws-d" /> Monitoreo activo</div>
        <div class="ws-time mono">{{ startedAtLabel }}</div>
        <div class="ws-pulse">
          <svg viewBox="0 0 200 26" preserveAspectRatio="none">
            <path
              d="M0 13 H40 l5 -9 l6 18 l5 -9 H100 l5 -9 l6 18 l5 -9 H200"
              fill="none"
              stroke="#c9a35a"
              stroke-width="1.4"
            />
          </svg>
        </div>

        <div class="ws-msg">
          Tu hora quedó guardada. <b>Los inicios tarde afectarán tu perfil.</b><br>
          Atentamente: <span class="ws-nm">Victor Guzmán</span><br><br>
          Dirección · Víctor Guzmán Fotografía
        </div>

        <div class="ws-date">{{ dateLabel }}</div>

        <div class="ws-arrow" :class="{ show: nagging }">
          Debes dar clic aquí para continuar ↓
        </div>
        <button
          type="button"
          class="ws-btn"
          :class="{ saving, shake: shaking }"
          :disabled="saving"
          @click.stop="confirmar"
        >
          <span class="ws-blink">⏱</span> Entendido · iniciar labor
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ws-overlay {
  position: fixed;
  inset: 0;
  z-index: 9990;
  background: radial-gradient(
    900px 600px at 50% 42%,
    rgba(0, 0, 0, 0.5),
    rgba(0, 0, 0, 0.84)
  );
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  cursor: not-allowed;
}
.ws-card {
  width: 100%;
  max-width: 440px;
  background: linear-gradient(180deg, #141315, #0e0d0f);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 22px;
  overflow: hidden;
  box-shadow: 0 45px 120px rgba(0, 0, 0, 0.75);
  cursor: auto;
}
.ws-pad {
  padding: 28px 26px 22px;
  text-align: center;
}
.mono {
  font-variant-numeric: tabular-nums;
}
.ws-lbl {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 10px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #c9a35a;
  font-weight: 800;
}
.ws-d {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #c9a35a;
  box-shadow: 0 0 8px #c9a35a;
  animation: ws-blink 1.3s infinite;
}
.ws-time {
  font-size: 48px;
  font-weight: 800;
  color: #c9a35a;
  letter-spacing: -1.5px;
  line-height: 1;
  margin: 12px 0 8px;
}
.ws-pulse {
  height: 26px;
  margin: 2px 0 16px;
  overflow: hidden;
  opacity: 0.7;
}
.ws-pulse svg {
  width: 200%;
  height: 100%;
  animation: ws-slide 2.4s linear infinite;
}
@keyframes ws-slide {
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
}
.ws-msg {
  font-size: 12.5px;
  color: #d8d6d2;
  line-height: 1.6;
}
.ws-msg b {
  color: #d98379;
  font-weight: 600;
}
.ws-nm {
  font-weight: 700;
  color: #eeece9;
}
.ws-date {
  font-size: 11px;
  color: #615f5a;
  margin-top: 12px;
}
.ws-arrow {
  text-align: center;
  color: #d98379;
  font-size: 11.5px;
  font-weight: 700;
  height: 0;
  opacity: 0;
  overflow: hidden;
  transition: opacity 0.25s, height 0.25s, margin 0.25s;
}
.ws-arrow.show {
  height: 18px;
  opacity: 1;
  margin-top: 10px;
  animation: ws-bob 1s infinite;
}
@keyframes ws-bob {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(4px); }
}
.ws-btn {
  position: relative;
  width: 100%;
  border: none;
  cursor: pointer;
  font-weight: 800;
  border-radius: 13px;
  padding: 15px;
  font-size: 13.5px;
  background: linear-gradient(180deg, #d8b56e, #c9a35a);
  color: #1b1408;
  letter-spacing: 0.3px;
  margin-top: 16px;
  animation: ws-pulse 1.6s ease-in-out infinite;
}
.ws-btn:hover {
  filter: brightness(1.06);
}
.ws-btn.saving {
  opacity: 0.6;
  cursor: default;
  animation: none;
}
@keyframes ws-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(201, 163, 90, 0); }
  50% { box-shadow: 0 0 24px 4px rgba(201, 163, 90, 0.45); }
}
.ws-blink {
  display: inline-block;
  animation: ws-blink 1s steps(2, start) infinite;
}
@keyframes ws-blink {
  50% { opacity: 0.3; }
}
.ws-btn.shake {
  animation: ws-shake 0.45s, ws-pulse 1.6s ease-in-out infinite;
}
@keyframes ws-shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-7px); }
  40% { transform: translateX(7px); }
  60% { transform: translateX(-5px); }
  80% { transform: translateX(5px); }
}
</style>
